import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "../../api/axiosInstance";
import useTherapistRoster from "../../hooks/useTherapistRoster";
import Env from '../../runtimeEnv';

const CreateMeetingForm = ({ initialMeeting }) => {
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, reset } = useForm({ defaultValues: {
    meetingName: initialMeeting?.meetingName || "",
    sessionTime: initialMeeting?.sessionTimeLocal || "",
    therapistEmail: initialMeeting?.therapist.email || "",
  } });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [clients, setClients] = useState(initialMeeting?.clients.map((client) => client.email) || [""]);

  const { therapists, rosterLoading, rosterError } = useTherapistRoster();
  const selectedTherapist = initialMeeting && therapists.find((therapist) =>
    therapist.email === initialMeeting.therapist.email || therapist.email_aliases?.includes(initialMeeting.therapist.email)
  );
  useEffect(() => {
    if (initialMeeting) setValue("therapist", selectedTherapist?.id || "calendar-therapist");
  }, [initialMeeting, selectedTherapist, setValue]);

  

  // Auto-fill therapist email when a therapist is selected
  const handleTherapistChange = (event) => {
    const therapistEmail = therapists.find((t) => t.id === event.target.value)?.email || "";
    setValue("therapistEmail", therapistEmail);
  };

  const addClient = () => setClients((prev) => [...prev, ""]);
  const removeClient = (index) =>
    setClients((prev) => prev.filter((_, i) => i !== index));
  const updateClient = (index, value) =>
    setClients((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

  const onSubmit = async (formData) => {
  setLoading(true);
  setMessage(null);

  try {
    // sanitize and normalize emails
    let validEmails = clients
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);

    // no duplicates
    const duplicates = validEmails.filter(
      (e, i) => validEmails.indexOf(e) !== i
    );
    if (duplicates.length > 0) {
      setMessage({
        type: "error",
        text: `Duplicate client email(s): ${duplicates.join(", ")}`,
      });
      return;
    }

    const therapistEmail =
      formData.therapistEmail?.trim().toLowerCase() || null;

    if (therapistEmail && validEmails.includes(therapistEmail)) {
      setMessage({
        type: "error",
        text: `Therapist email cannot also be a client.`,
      });
      return;
    }

    if (validEmails.length === 0) {
      setMessage({
        type: "error",
        text: "Please provide at least one client email.",
      });
      return;
    }

    if (!formData.meetingName) formData.meetingName = "Therapy Session";

    const payload =
      validEmails.length === 1
        ? {
            email: validEmails[0],
            therapistEmail,
            sessionTime: formData.sessionTime,
            meetingName: formData.meetingName,
          }
        : {
            emails: validEmails,
            therapistEmail,
            sessionTime: formData.sessionTime,
            meetingName: formData.meetingName,
          };



    if (initialMeeting) {
      await axios.put(`${Env.REACT_APP_PYTHON_API_URL}/api/admin/calendar/reschedule`, {
        meetingID: initialMeeting.meetingID,
        clientEmails: validEmails,
        therapistEmail,
        sessionTime: formData.sessionTime,
        meetingName: formData.meetingName,
      });
      navigate("/admin", { state: {
        calendarMonth: formData.sessionTime.slice(0, 7),
        calendarNotice: "Session rescheduled successfully.",
      } });
      return;
    }

    await axios.post(
      `${Env.REACT_APP_PYTHON_API_URL}/api/admin/schedule`,
      payload
    , { withCredentials: true });

    setMessage({
      type: "success",
      text: "Session scheduled successfully.",
    });

    reset();
    setClients([""]);
  } catch (error) {
    console.error("❌ Error scheduling session:");
    setMessage({
      type: "error",
      text: error.response?.data?.message || "Failed to schedule session.",
    });
  } finally {
    setLoading(false);
  }
};



  return (
    <>
      {message && (
        <div
          className={`p-2 text-center ${
            message.type === "success"
              ? "text-green-600"
              : "text-red-600"
          }`}
        >
          {message.text}
        </div>
      )}

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <p className="text-sm text-gray-600">Session times are in Eastern time.</p>
        {/* Multiple Client Emails */}
        <div className="space-y-2">
          <label className="block font-medium">
            Client Emails
          </label>
          {clients.map((email, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="email"
                aria-label={`Client email ${index + 1}`}
                readOnly={Boolean(initialMeeting)}
                value={email}
                onChange={(e) =>
                  updateClient(index, e.target.value)
                }
                placeholder="Client Email"
                className="input-field flex-grow"
                required={index === 0}
              />
              {!initialMeeting && index > 0 && (
                <button
                  type="button"
                  onClick={() => removeClient(index)}
                  className="px-2 rounded bg-red-500 text-white"
                >
                  🗑️
                </button>
              )}
              {!initialMeeting && index === clients.length - 1 && (
                <button
                  type="button"
                  onClick={addClient}
                  className="px-2 rounded bg-blue-500 text-white"
                >
                  +
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Meeting Name */}
        <input
          aria-label="Meeting name"
          {...register("meetingName")}
          placeholder="Meeting Name"
          required
          className="input-field"
        />

        {/* Session Time */}
        <input
          type="datetime-local"
          aria-label="Session date and time (Eastern)"
          {...register("sessionTime")}
          required
          className="input-field"
        />

        {!initialMeeting && rosterLoading && <p role="status">Loading therapists…</p>}
        {!initialMeeting && rosterError && <p role="alert">{rosterError}</p>}
        {/* Therapist Selection */}
        <select
          aria-label="Therapist"
          disabled={Boolean(initialMeeting)}
          {...register("therapist")}
          required
          className="input-field"
          onChange={handleTherapistChange}
        >
          <option value="">Select a Therapist</option>
          {initialMeeting && !selectedTherapist && <option value="calendar-therapist">{initialMeeting.therapist.name}</option>}
          {therapists.map((therapist, index) => (
            <option key={therapist.id} value={therapist.id}>
              {therapist.name}
            </option>
          ))}
        </select>

        {/* Auto-filled Therapist Email */}
        <input
          {...register("therapistEmail")}
          aria-label="Therapist email"
          placeholder="Therapist Email"
          readOnly
          className="input-field bg-gray-200 cursor-not-allowed"
        />

        {/* Submit Button */}
        <button
          type="submit"
          className="button-primary"
          disabled={loading || (!initialMeeting && (rosterLoading || Boolean(rosterError)))}
        >
          {loading
            ? "Scheduling..."
            : initialMeeting ? "Save Reschedule" : "Schedule Session"}
        </button>
      </form>
    </>
  );
};

export default CreateMeetingForm;